import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { fmt, fmtMonth, THIS_MONTH, THIS_SESSION } from './payrollUtils';
import { IcoX, IcoCheck, IcoRefresh, IcoFilter, IcoPlus,
         IcoDollar, IcoChevL, IcoChevR, IcoAlert, IcoUsers,
         SkeletonRow, CSS, EmployeeRole } from '../PayrollManagement';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface Advance {
  id: string;
  employee_id: string;
  employee_code?: string | null;
  employee_role: EmployeeRole;
  employee_name: string | null;
  month_year: string;
  session: string;
  amount: number;
  payment_mode: 'cash' | 'bank_transfer' | 'cheque';
  payment_date: string;
  transaction_ref: string | null;
  reason: string | null;
  recorded_by: string;
  payroll_record_id: string | null;
  status: 'pending' | 'deducted' | 'cancelled';
  created_at: string;
}

interface Employee {
  employee_id: string;
  employee_code?: string | null;
  user_id: string;
  name: string;
  email: string | null;
  employee_role: EmployeeRole;
  job_profile: string;
}

interface PaginationData {
  current_page: number;
  total_pages: number;
  total_items: number;
  per_page: number;
  has_next: boolean;
  has_previous: boolean;
}

interface AdvanceForm {
  employee_id: string;
  employee_role: EmployeeRole | '';
  month_year: string;
  session: string;
  amount: string;
  payment_mode: 'cash' | 'bank_transfer' | 'cheque' | '';
  payment_date: string;
  transaction_ref: string;
  reason: string;
}

const EMPTY_FORM: AdvanceForm = {
  employee_id: '',
  employee_role: '',
  month_year: THIS_MONTH,
  session: THIS_SESSION,
  amount: '',
  payment_mode: '',
  payment_date: new Date().toISOString().substring(0, 10),
  transaction_ref: '',
  reason: '',
};

// ─────────────────────────────────────────────────────────────────────────────
// Inline icons
// ─────────────────────────────────────────────────────────────────────────────
const Ico = ({ d, size = 16, color = 'currentColor', style = {}, strokeWidth = 1.8 }: {
  d: string | string[]; size?: number; color?: string; style?: React.CSSProperties; strokeWidth?: number;
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0, display: 'block', ...style }}>
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);
const IcoBan      = (p: any) => <Ico {...p} d={['M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z', 'M4.93 4.93l14.14 14.14']} />;
const IcoArrowDL  = (p: any) => <Ico {...p} d={['M12 5v14', 'M19 12l-7 7-7-7']} />;

// ─────────────────────────────────────────────────────────────────────────────
// Status badge
// ─────────────────────────────────────────────────────────────────────────────
const AdvanceBadge: React.FC<{ status: Advance['status'] }> = ({ status }) => {
  const map = {
    pending:   { cls: 'pr-badge--pending',   icon: <Ico d="M12 6v6l4 2M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" size={9} color="#b45309" />, label: 'Pending' },
    deducted:  { cls: 'pr-badge--paid',      icon: <IcoCheck size={9} color="#15803d" />, label: 'Deducted' },
    cancelled: { cls: 'pr-badge--cancelled', icon: <IcoAlert size={9} color="#dc2626" />, label: 'Cancelled' },
  };
  const m = map[status] ?? map.pending;
  return <span className={`pr-badge ${m.cls}`}>{m.icon} {m.label}</span>;
};

// ─────────────────────────────────────────────────────────────────────────────
// Employee search dropdown (reused pattern from PayrollSalaryModal)
// ─────────────────────────────────────────────────────────────────────────────
const EmployeeDropdown: React.FC<{
  employees: Employee[];
  loading: boolean;
  selected: Employee | null;
  onSelect: (e: Employee) => void;
  onClear: () => void;
  error?: string;
}> = ({ employees, loading, selected, onSelect, onClear, error }) => {
  const [search, setSearch]     = useState('');
  const [open,   setOpen]       = useState(false);
  const ref                     = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = search.trim()
    ? employees.filter(e =>
        e.name.toLowerCase().includes(search.toLowerCase()) ||
        e.job_profile.toLowerCase().includes(search.toLowerCase()) ||
        String(e.employee_id).includes(search) ||
        (e.employee_code ?? '').toLowerCase().includes(search.toLowerCase())
      )
    : employees;

  if (selected) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 11, border: '1.5px solid #3b82f6', background: 'rgba(59,130,246,.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, background: 'linear-gradient(135deg,#1e3a8a,#3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: 'white' }}>
            {selected.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{selected.name}</div>
            <div style={{ fontSize: 11, color: '#9ca3af' }}>{selected.job_profile} · ID #{selected.employee_code ?? selected.employee_id}</div>
          </div>
        </div>
        <button onClick={onClear} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', borderRadius: 6 }}>
          <IcoX size={14} color="#9ca3af" />
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        className="pr-input"
        placeholder={loading ? 'Loading employees…' : 'Search by name, role or ID…'}
        value={search}
        disabled={loading}
        style={{ width: '100%' }}
        onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
      />
      {loading && (
        <IcoRefresh size={13} color="#9ca3af" style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', animation: 'pr-spin 1s linear infinite' }} />
      )}
      {open && (
        <div className="pr-scroll" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: 'white', borderRadius: 13, border: '1px solid #e5e7eb', boxShadow: '0 8px 30px rgba(0,0,0,.12)', maxHeight: 220, overflowY: 'auto', marginTop: 4 }}>
          {filtered.length === 0
            ? <div style={{ padding: '14px', fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>No employees found.</div>
            : filtered.map(emp => (
              <div key={`${emp.employee_role}-${emp.employee_id}`}
                onClick={() => { onSelect(emp); setSearch(''); setOpen(false); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', cursor: 'pointer', borderBottom: '1px solid #f9fafb' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(59,130,246,.05)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: 'linear-gradient(135deg,#1e3a8a,#3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: 'white' }}>
                  {emp.name.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{emp.name}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>{emp.job_profile}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af' }}>#{emp.employee_code ?? emp.employee_id}</span>
              </div>
            ))
          }
        </div>
      )}
      {error && <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600, marginTop: 3, display: 'block' }}>{error}</span>}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Add Advance Modal
// ─────────────────────────────────────────────────────────────────────────────
const AddAdvanceModal: React.FC<{
  onSaved: () => void;
  onClose: () => void;
  toast: React.RefObject<Toast | null>;
}> = ({ onSaved, onClose, toast }) => {
  const [form,      setForm]      = useState<AdvanceForm>(EMPTY_FORM);
  const [saving,    setSaving]    = useState(false);
  const [errors,    setErrors]    = useState<Partial<Record<keyof AdvanceForm, string>>>({});
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState<Employee | null>(null);

  useEffect(() => {
    const load = async () => {
      setEmpLoading(true);
      try {
        const res = await apiClient.get('/payroll/employees');
        if (res.data.status === 'success') setEmployees(res.data.data ?? []);
      } catch { /* silent */ } finally { setEmpLoading(false); }
    };
    load();
  }, []);

  const set = (k: keyof AdvanceForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(f => ({ ...f, [k]: e.target.value }));
    setErrors(er => ({ ...er, [k]: undefined }));
  };

  const selectEmployee = (emp: Employee) => {
    setSelectedEmp(emp);
    setForm(f => ({ ...f, employee_id: String(emp.employee_id), employee_role: emp.employee_role }));
    setErrors(er => ({ ...er, employee_id: undefined }));
  };

  const clearEmployee = () => {
    setSelectedEmp(null);
    setForm(f => ({ ...f, employee_id: '', employee_role: '' }));
  };

  const validate = (): boolean => {
    const e: Partial<Record<keyof AdvanceForm, string>> = {};
    if (!form.employee_id)   e.employee_id   = 'Select an employee';
    if (!form.month_year)    e.month_year    = 'Required';
    if (!form.session)       e.session       = 'Required';
    if (!form.amount || isNaN(+form.amount) || +form.amount <= 0) e.amount = 'Enter a valid amount';
    if (!form.payment_mode)  e.payment_mode  = 'Select payment mode';
    if (!form.payment_date)  e.payment_date  = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await apiClient.post('/payroll/advances', {
        employee_id:     form.employee_id,
        employee_role:   form.employee_role,
        month_year:      form.month_year,
        session:         form.session,
        amount:          +form.amount,
        payment_mode:    form.payment_mode,
        payment_date:    form.payment_date,
        transaction_ref: form.transaction_ref || undefined,
        reason:          form.reason          || undefined,
      });
      if (res.data.status === 'success') {
        toast.current?.show({ severity: 'success', summary: 'Advance recorded', detail: `${fmt(+form.amount)} advance recorded successfully.`, life: 3000 });
        onSaved();
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: res.data.message, life: 4000 });
      }
    } catch (e: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: e?.response?.data?.message || 'Failed to record advance', life: 4000 });
    } finally { setSaving(false); }
  };

  const err = (k: keyof AdvanceForm) => errors[k]
    ? <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600, marginTop: 2, display: 'block' }}>{errors[k]}</span>
    : null;

  return (
    <div className="pr-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="pr-modal" style={{ maxWidth: 560 }}>

        {/* Header */}
        <div className="pr-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: 'linear-gradient(135deg,#b45309,#f59e0b)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 3px 8px rgba(180,83,9,.3)', flexShrink: 0 }}>
              <IcoArrowDL size={17} color="white" />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Record advance</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Will be deducted when payroll is generated</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', borderRadius: 8 }}>
            <IcoX size={18} color="#9ca3af" />
          </button>
        </div>

        {/* Body */}
        <div className="pr-modal-body">

          {/* Employee */}
          <div className="pr-field">
            <label className="pr-label">Employee *</label>
            <EmployeeDropdown
              employees={employees}
              loading={empLoading}
              selected={selectedEmp}
              onSelect={selectEmployee}
              onClear={clearEmployee}
              error={errors.employee_id}
            />
          </div>

          {/* Month + Session */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="pr-field">
              <label className="pr-label">Month *</label>
              <input type="month" className="pr-input" value={form.month_year} onChange={set('month_year')} />
              {err('month_year')}
            </div>
            <div className="pr-field">
              <label className="pr-label">Session *</label>
              <input type="text" className="pr-input" placeholder="e.g. 2025-26" value={form.session} onChange={set('session')} />
              {err('session')}
            </div>
          </div>

          {/* Amount */}
          <div className="pr-field">
            <label className="pr-label">Advance amount (₹) *</label>
            <input type="number" min="1" className="pr-input" placeholder="0" value={form.amount} onChange={set('amount')} style={{ fontSize: 16, fontWeight: 700 }} />
            {err('amount')}
          </div>

          {/* Payment mode + date */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="pr-field">
              <label className="pr-label">Payment mode *</label>
              <select className="pr-select" value={form.payment_mode} onChange={set('payment_mode')} style={{ width: '100%' }}>
                <option value="">— Select —</option>
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank transfer</option>
                <option value="cheque">Cheque</option>
              </select>
              {err('payment_mode')}
            </div>
            <div className="pr-field">
              <label className="pr-label">Payment date *</label>
              <input type="date" className="pr-input" value={form.payment_date} onChange={set('payment_date')} />
              {err('payment_date')}
            </div>
          </div>

          {/* Transaction ref */}
          <div className="pr-field">
            <label className="pr-label">Transaction / cheque ref (optional)</label>
            <input type="text" className="pr-input" placeholder="UTR, cheque number…" value={form.transaction_ref} onChange={set('transaction_ref')} />
          </div>

          {/* Reason */}
          <div className="pr-field">
            <label className="pr-label">Reason (optional)</label>
            <textarea
              className="pr-input"
              placeholder="e.g. Medical emergency, festival advance…"
              value={form.reason}
              onChange={set('reason') as any}
              rows={2}
              style={{ resize: 'vertical', fontFamily: 'DM Sans, sans-serif' }}
            />
          </div>

          {/* Info banner */}
          <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(245,158,11,.07)', border: '1px solid rgba(245,158,11,.2)', fontSize: 12, color: '#b45309', fontWeight: 500, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <IcoAlert size={14} color="#b45309" style={{ marginTop: 1, flexShrink: 0 }} />
            This advance will be automatically deducted from the employee's net salary when payroll is generated for {form.month_year ? fmtMonth(form.month_year) : 'the selected month'}.
          </div>
        </div>

        {/* Footer */}
        <div className="pr-modal-footer">
          <button className="pr-btn pr-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="pr-btn"
            style={{ background: 'linear-gradient(135deg,#b45309,#f59e0b)', color: 'white', boxShadow: '0 3px 10px rgba(180,83,9,.28)' }}
            onClick={handleSave}
            disabled={saving}
          >
            {saving
              ? <IcoRefresh size={14} color="white" style={{ animation: 'pr-spin 1s linear infinite' }} />
              : <IcoCheck size={14} color="white" />}
            {saving ? 'Saving…' : 'Record advance'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main PayrollAdvances component
// ─────────────────────────────────────────────────────────────────────────────
interface Props {
  toast: React.RefObject<Toast | null>;
}

const PayrollAdvances: React.FC<Props> = ({ toast }) => {
  const [advances,    setAdvances]    = useState<Advance[]>([]);
  const [loading,     setLoading]     = useState(false);
  const [modalOpen,   setModalOpen]   = useState(false);
  const [cancelling,  setCancelling]  = useState<string | null>(null);
  const [pagination,  setPagination]  = useState<PaginationData>({
    current_page: 1, total_pages: 1, total_items: 0, per_page: 20, has_next: false, has_previous: false,
  });

  // Filters
  const [filterMonth,  setFilterMonth]  = useState(THIS_MONTH);
  const [filterSession, setFilterSession] = useState(THIS_SESSION);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterEmpId,  setFilterEmpId]  = useState('');
  const [page,         setPage]         = useState(1);
  const [pageSize,     setPageSize]     = useState(20);

  const fetchAdvances = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, limit: pageSize };
      if (filterMonth)   params.month_year = filterMonth;
      if (filterSession) params.session    = filterSession;
      if (filterStatus)  params.status     = filterStatus;
      if (filterEmpId)   params.employee_id = filterEmpId.trim();
      const res = await apiClient.get('/payroll/advances', { params });
      if (res.data.status === 'success') {
        setAdvances(res.data.data.advances ?? []);
        setPagination(res.data.data.pagination);
      }
    } catch (e: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: e?.response?.data?.message || 'Failed to load advances', life: 4000 });
    } finally { setLoading(false); }
  }, [page, pageSize, filterMonth, filterSession, filterStatus, filterEmpId, toast]);

  useEffect(() => { fetchAdvances(); }, [fetchAdvances]);

  const handleCancel = async (id: string) => {
    if (!window.confirm('Cancel this advance? This cannot be undone.')) return;
    setCancelling(id);
    try {
      const res = await apiClient.post(`/payroll/advances/${id}/cancel`);
      if (res.data.status === 'success') {
        toast.current?.show({ severity: 'success', summary: 'Cancelled', detail: 'Advance cancelled.', life: 3000 });
        fetchAdvances();
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: res.data.message, life: 4000 });
      }
    } catch (e: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: e?.response?.data?.message || 'Failed', life: 4000 });
    } finally { setCancelling(null); }
  };

  const clearFilters = () => {
    setFilterMonth(THIS_MONTH); setFilterSession(THIS_SESSION);
    setFilterStatus(''); setFilterEmpId(''); setPage(1);
  };

  const totalPending  = advances.filter(a => a.status === 'pending').reduce((s, a)  => s + Number(a.amount), 0);
  const totalDeducted = advances.filter(a => a.status === 'deducted').reduce((s, a) => s + Number(a.amount), 0);
  const pendingCount  = advances.filter(a => a.status === 'pending').length;

  const pageNums = () => {
    const total = pagination.total_pages, cur = pagination.current_page;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (cur > 3) pages.push('...');
    for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) pages.push(i);
    if (cur < total - 2) pages.push('...');
    pages.push(total);
    return pages;
  };

  return (
    <>
      <style>{CSS}</style>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12, marginBottom: 16 }}>
        {[
          { label: 'Total advances',    val: pagination.total_items,  color: '#b45309', bg: 'rgba(245,158,11,.08)' },
          { label: 'Pending amount',    val: fmt(totalPending),        color: '#b45309', bg: 'rgba(245,158,11,.08)' },
          { label: 'Deducted amount',   val: fmt(totalDeducted),       color: '#15803d', bg: 'rgba(34,197,94,.08)'  },
          { label: 'Pending advances',  val: pendingCount,             color: '#1d4ed8', bg: 'rgba(29,78,216,.08)'  },
        ].map((s, i) => (
          <div key={i} style={{ padding: '12px 16px', borderRadius: 14, border: '1px solid #e5e7eb', background: s.bg, animation: `pr-slideUp .3s ease ${i * 0.05}s both` }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ background: 'white', borderRadius: 16, padding: '14px 18px', marginBottom: 14, border: '1px solid #e5e7eb', display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <IcoFilter size={13} color="#6b7280" />
          <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px' }}>Filters</span>
        </div>
        <div className="pr-field">
          <label className="pr-label">Month</label>
          <input type="month" className="pr-input" value={filterMonth} onChange={e => { setFilterMonth(e.target.value); setPage(1); }} style={{ width: 160 }} />
        </div>
        <div className="pr-field">
          <label className="pr-label">Session</label>
          <input type="text" className="pr-input" placeholder="e.g. 2025-26" value={filterSession} onChange={e => { setFilterSession(e.target.value); setPage(1); }} style={{ width: 120 }} />
        </div>
        <div className="pr-field">
          <label className="pr-label">Status</label>
          <select className="pr-select" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="deducted">Deducted</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="pr-field">
          <label className="pr-label">Employee ID</label>
          <input type="text" className="pr-input" placeholder="Any" value={filterEmpId} onChange={e => { setFilterEmpId(e.target.value); setPage(1); }} style={{ width: 150 }} />
        </div>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button className="pr-btn pr-btn--ghost" style={{ padding: '8px 12px' }} onClick={clearFilters} title="Clear"><IcoX size={13} color="#0f766e" /></button>
          <button className="pr-btn pr-btn--ghost" style={{ padding: '8px 12px' }} onClick={fetchAdvances} title="Refresh">
            <IcoRefresh size={13} color="#0f766e" style={{ animation: loading ? 'pr-spin 1s linear infinite' : 'none' }} />
          </button>
          <button className="pr-btn pr-btn--primary" onClick={() => setModalOpen(true)} style={{ background: 'linear-gradient(135deg,#b45309,#f59e0b)', boxShadow: '0 3px 10px rgba(180,83,9,.28)' }}>
            <IcoPlus size={13} color="white" /> Record advance
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: 20, border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 2px 16px rgba(15,118,110,.07)' }}>

        {/* Table header bar */}
        <div style={{ padding: '13px 20px', borderBottom: '1px solid #f3f4f6', background: 'linear-gradient(135deg,#fff,#fafbfc)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(245,158,11,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IcoArrowDL size={14} color="#b45309" />
            </div>
            <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
              {loading ? 'Loading…' : `${pagination.total_items} advances`}
              {filterMonth && <span style={{ color: '#b45309', marginLeft: 6 }}>for {fmtMonth(filterMonth)}</span>}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>Per page</span>
            <select className="pr-select" value={pageSize} style={{ padding: '5px 8px', fontSize: 12 }} onChange={e => { setPageSize(+e.target.value); setPage(1); }}>
              {[10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="pr-scroll" style={{ overflowX: 'auto' }}>
          <table className="pr-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Employee</th>
                <th>Role</th>
                <th>Month</th>
                <th>Amount</th>
                <th>Payment mode</th>
                <th>Payment date</th>
                <th>Ref</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} i={i} cols={11} />)
                : advances.length === 0
                  ? (
                    <tr><td colSpan={11}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', gap: 12 }}>
                        <div style={{ width: 54, height: 54, borderRadius: 14, background: 'rgba(245,158,11,.06)', border: '1.5px dashed rgba(245,158,11,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <IcoArrowDL size={24} color="#f59e0b" />
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#374151' }}>No advances found</div>
                        <div style={{ fontSize: 13, color: '#9ca3af' }}>Record an advance using the button above.</div>
                      </div>
                    </td></tr>
                  )
                  : advances.map((adv, i) => (
                    <tr key={adv.id} style={{ animationDelay: `${i * 0.04}s` }}>
                      <td>
                        <div style={{ width: 28, height: 28, background: '#f9fafb', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#6b7280', border: '1px solid #e5e7eb' }}>
                          {(page - 1) * pageSize + i + 1}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{adv.employee_name ?? `#${adv.employee_id}`}</div>
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>ID #{adv.employee_code ?? adv.employee_id}</div>
                      </td>
                      <td><span className="pr-role">{adv.employee_role}</span></td>
                      <td style={{ fontSize: 13, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>
                        {fmtMonth(adv.month_year)}
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>{adv.session}</div>
                      </td>
                      <td>
                        <div style={{ fontSize: 14, fontWeight: 800, color: '#b45309' }}>{fmt(adv.amount)}</div>
                      </td>
                      <td style={{ fontSize: 12, color: '#374151', textTransform: 'capitalize' }}>
                        {adv.payment_mode.replace('_', ' ')}
                      </td>
                      <td style={{ fontSize: 12, color: '#374151', whiteSpace: 'nowrap' }}>{adv.payment_date}</td>
                      <td style={{ fontSize: 12, color: '#9ca3af', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {adv.transaction_ref ?? '—'}
                      </td>
                      <td style={{ fontSize: 12, color: '#6b7280', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {adv.reason ?? '—'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <AdvanceBadge status={adv.status} />
                          {adv.status === 'deducted' && adv.payroll_record_id && (
                            <span style={{ fontSize: 10, color: '#9ca3af' }}>Payroll #{adv.payroll_record_id.slice(-6)}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        {adv.status === 'pending' && (
                          <button
                            className="pr-btn pr-btn--danger"
                            style={{ padding: '4px 10px', fontSize: 12 }}
                            disabled={cancelling === adv.id}
                            onClick={() => handleCancel(adv.id)}
                            title="Cancel advance"
                          >
                            {cancelling === adv.id
                              ? <IcoRefresh size={11} color="#dc2626" style={{ animation: 'pr-spin 1s linear infinite' }} />
                              : <IcoBan size={11} color="#dc2626" />}
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div style={{ padding: '13px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>
              Page {pagination.current_page} of {pagination.total_pages} · {pagination.total_items} total
            </span>
            <div style={{ display: 'flex', gap: 5 }}>
              <button className="pr-pg-btn" disabled={!pagination.has_previous} onClick={() => setPage(p => p - 1)}><IcoChevL size={13} color="#374151" /></button>
              {pageNums().map((n, i) =>
                n === '...'
                  ? <span key={i} style={{ width: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13 }}>…</span>
                  : <button key={i} className={`pr-pg-btn${n === pagination.current_page ? ' pr-pg-btn--active' : ''}`} onClick={() => setPage(n as number)}>{n}</button>
              )}
              <button className="pr-pg-btn" disabled={!pagination.has_next} onClick={() => setPage(p => p + 1)}><IcoChevR size={13} color="#374151" /></button>
            </div>
          </div>
        )}
      </div>

      {/* Add advance modal */}
      {modalOpen && (
        <AddAdvanceModal
          onSaved={() => { setModalOpen(false); fetchAdvances(); }}
          onClose={() => setModalOpen(false)}
          toast={toast}
        />
      )}
    </>
  );
};

export default PayrollAdvances;
